import React, { useCallback, useEffect, useMemo } from 'react';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { Box, ButtonV2, DialogV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import Confetti from 'react-confetti';
import { useHeaderLayoutUpgradeDialogController } from './useHeaderLayoutUpgradeDialog.controller';
import { Skeleton } from '@shared/components/Skeleton';
import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';
import { EventDesignPurpose, useActiveWebsiteHeaderPresentationLayoutQuery } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { noop } from 'lodash-es';

const FALLBACK_THUMBNAIL_IMAGE_URL =
  'https://withjoy.com/assets/public/marcom-prod/wedding-website-gallery/gallery_thumbnails/default_preview_images/brannan-wanderlust_meadow-videobackground.png?v=1638397480';

interface ContentProps
  extends Readonly<{
    eventHandle: string;
    title: string;
    description: string;
  }> {}

const Content: React.FC<ContentProps> = ({ eventHandle, title, description }) => {
  const { tq } = useTranslation('headerLayoutUpgrade');

  const { eventExposure } = useWebsitePublishCapabilityFeature();
  const shouldUseLivePurpose = eventExposure.value;

  const { data: headerPresentationLayout, loading: isLoading } = useActiveWebsiteHeaderPresentationLayoutQuery({
    variables: {
      eventHandle,
      purpose: shouldUseLivePurpose ? EventDesignPurpose.live : EventDesignPurpose.draft,
    },
    skip: !eventHandle || eventExposure.isLoading,
    batchMode: 'fast',
  });

  const { thumbnailImageUrl } = useMemo(() => {
    const headerPresentationLayoutData = headerPresentationLayout?.eventByName?.eventDesign?.activeWebsiteHeaderPresentationLayout?.dataJSON as SupportedHeaderPhotoLayoutSchema;

    return {
      thumbnailImageUrl: headerPresentationLayoutData?.presentationLayout === 'videoBackground' ? headerPresentationLayoutData?.video?.placeholderImageUrl : '',
    };
  }, [headerPresentationLayout]);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      gap={7}
    >
      <Skeleton
        width="100%"
        maxWidth="100%"
        aspectRatio="16/9"
        borderRadius={pxToRem(8)}
        isReady={!isLoading}
        overflow="hidden"
      >
        <Box
          as="img"
          src={thumbnailImageUrl || FALLBACK_THUMBNAIL_IMAGE_URL}
          alt={tq`Video Background Thumbnail`}
          width="100%"
          aspectRatio="16/9"
          objectFit="cover"
          background="lightgray"
        />
      </Skeleton>
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={4}
      >
        <TextV2
          typographyVariant="hed6"
          textAlign="center"
          width="100%"
          fontWeight={600}
        >
          {title}
        </TextV2>
        <TextV2
          as="p"
          typographyVariant="body2"
          color="mono12"
          textAlign="center"
        >
          {description}
        </TextV2>
      </Flex>
    </Flex>
  );
};

interface HeaderLayoutUpgradeDialogProps
  extends Readonly<{
    isOpen: boolean;
    onClose: () => void;
    secondaryAction?: 'turn-off';
    onDialogOpen: () => void;
    onUpgradeClick: () => void;
    onNoThanksClick: () => void;
    onAllDoneClick: () => void;
  }> {}

export const HeaderLayoutUpgradeDialog: React.FC<HeaderLayoutUpgradeDialogProps> = ({
  isOpen,
  onClose,
  secondaryAction,
  onDialogOpen,
  onUpgradeClick,
  onNoThanksClick,
  onAllDoneClick,
}) => {
  const { eventHandle } = useEventInfo();
  const { tq } = useTranslation('headerLayoutUpgrade');

  const { screen, isUpgrading, handleUpgradeClick } = useHeaderLayoutUpgradeDialogController({ isOpen });

  useEffect(() => {
    if (isOpen) {
      onDialogOpen();
    }
  }, [isOpen, onDialogOpen]);

  const handleUpgradeButtonClick = useCallback(async () => {
    onUpgradeClick();
    await handleUpgradeClick();
  }, [handleUpgradeClick, onUpgradeClick]);

  const handleNoThanksClick = useCallback(() => {
    onNoThanksClick();
    onClose();
  }, [onClose, onNoThanksClick]);

  const handleAllDoneClick = useCallback(() => {
    onAllDoneClick();
    onClose();
  }, [onClose, onAllDoneClick]);

  return (
    <>
      <ResponsiveDialog
        isOpen={isOpen}
        onClose={noop}
        disableCloseOnEscapeKeyClick
        disableCloseOnOutsideClick
        hideDivider
        hideFooterDivider
        hasCloseButton={false}
        removeBodyPadding
        drawerOverrides={{
          ...drawerOverrides,
          Content: {
            props: {
              ...drawerOverrides.Content?.props,
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              overflow: 'hidden',
            },
          },
          Footer: { props: { paddingTop: [7, 7, 7], paddingX: [7, 9, 9], paddingBottom: [7, 9, 9] } },
          CloseButton: {
            props: { position: 'absolute', top: 2, right: 2, width: pxToRem(32), height: pxToRem(32), minWidth: 0, padding: 0 },
          },
        }}
        dialogOverrides={{
          Footer: { props: { paddingTop: [6, 7, 7], paddingX: [7, 9, 9], paddingBottom: [7, 9, 9] } },
          Body: { props: { paddingX: [7, 9, 9], paddingTop: [7, 9, 9], paddingBottom: [0, 0, 0] } },
          Content: { props: { width: ['100%', pxToRem(540)], overflow: 'hidden' } },
          CloseButton: {
            props: { position: 'absolute', top: 3, right: 5, width: pxToRem(32), height: pxToRem(32), minWidth: 0, padding: 0 },
          },
        }}
        renderFooter={() => (
          <Flex
            flexDirection="column"
            alignItems="center"
            width="100%"
            gap={5}
          >
            {screen === 'offer' ? (
              <>
                <ButtonV2
                  size="lg"
                  maxWidth={pxToRem(380)}
                  intent="newPrimary"
                  shape="rounded"
                  width="100%"
                  loading={isUpgrading}
                  onClick={handleUpgradeButtonClick}
                >
                  {tq`Upgrade for $9.90`}
                </ButtonV2>
                {secondaryAction === 'turn-off' && (
                  <ButtonV2
                    size="lg"
                    maxWidth={pxToRem(380)}
                    intent="neutral"
                    variant="ghost"
                    shape="rounded"
                    onClick={handleNoThanksClick}
                    width="100%"
                  >
                    {tq`No thanks, turn off video header`}
                  </ButtonV2>
                )}
              </>
            ) : (
              <>
                <ButtonV2
                  size="lg"
                  maxWidth={pxToRem(380)}
                  intent="newPrimary"
                  shape="rounded"
                  onClick={handleAllDoneClick}
                  width="100%"
                >
                  {tq`All Done`}
                </ButtonV2>
              </>
            )}
          </Flex>
        )}
      >
        {secondaryAction !== 'turn-off' && <DialogV2.CloseButton onClick={onClose} />}
        <Content
          eventHandle={eventHandle || ''}
          title={screen === 'offer' ? tq`Make your site come to life with a video background!` : tq`We appreciate you - enjoy this free upgrade.`}
          description={
            screen === 'offer'
              ? tq`Want to keep this stunning video design? Upgrade now to make your website truly stand out.`
              : tq`Our premium feature (worth $9.90) is yours at no cost. Thank you for choosing Joy.`
          }
        />
        {screen === 'success' ? (
          <Confetti
            recycle={false}
            numberOfPieces={500}
          />
        ) : null}
      </ResponsiveDialog>
    </>
  );
};
